import type { MemberEnvironment } from '@cortex/domain-ac-mens-hub-api';
import { useTranslation } from 'react-i18next';

/** @graph-connects none */
const tabBase =
  'flex items-center gap-2 border-b-2 px-6 py-2 text-[13px] font-semibold transition-colors -mb-px';

/**
 * 会員一覧の環境タブ（本番 / STG）と、STG 選択中の注意帯。
 *
 * タブは**持っている権限のものだけ**出す。本番権限が無い人に本番タブを見せても
 * 押した先で 403 になるだけで、押せることに意味が無い。
 *
 * STG を選んでいる間は帯を常時表示する。テストのつもりで本番会員を見ていた、という
 * 取り違えがこの画面の最大のリスクなので、スクロールしても消えない位置に置く。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 会員一覧の環境タブ。権限に応じて本番/STGタブを出し分け、STG選択中は取り違え防止の帯を常時表示する。
 * @graph-connects none
 */
export function MemberEnvironmentTabs({
  environment,
  canViewProduction,
  canViewStaging,
  onChange,
}: {
  environment: MemberEnvironment;
  canViewProduction: boolean;
  canViewStaging: boolean;
  onChange: (environment: MemberEnvironment) => void;
}) {
  const { t } = useTranslation();

  // 片方の権限しか無い人にタブを1つだけ出しても選びようが無く、画面が狭くなるだけ
  const showTabs = canViewProduction && canViewStaging;

  return (
    <>
      {environment === 'staging' && (
        <div
          role="status"
          className="mb-4 flex flex-wrap items-center gap-2.5 rounded-lg border border-[var(--color-warning)] bg-[var(--color-warning-bg)] px-3.5 py-2.5 text-[13px] font-semibold text-[var(--color-warning)]"
        >
          <span
            className="h-2 w-2 flex-none rounded-full bg-[var(--color-warning)]"
            aria-hidden="true"
          />
          {t('memberList.stagingBanner')}
          <span className="ml-auto text-[11px] font-normal text-[var(--color-text-secondary)]">
            {t('memberList.stagingBannerNote')}
          </span>
        </div>
      )}

      {showTabs && (
        <div role="tablist" className="mb-4 flex border-b border-[var(--color-border)]">
          <button
            type="button"
            role="tab"
            aria-selected={environment === 'production'}
            onClick={() => {
              onChange('production');
            }}
            className={`${tabBase} ${
              environment === 'production'
                ? 'border-[var(--color-accent-primary)] text-[var(--color-accent-primary)]'
                : 'border-transparent text-[var(--color-text-muted)]'
            }`}
          >
            {t('memberList.environmentProduction')}
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={environment === 'staging'}
            onClick={() => {
              onChange('staging');
            }}
            className={`${tabBase} ${
              environment === 'staging'
                ? 'border-[var(--color-warning)] text-[var(--color-warning)]'
                : 'border-transparent text-[var(--color-text-muted)]'
            }`}
          >
            {t('memberList.environmentStaging')}
          </button>
        </div>
      )}
    </>
  );
}
